import { XMLParser, XMLBuilder } from 'fast-xml-parser';

const parserOptions = {
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  allowBooleanAttributes: true,
  parseAttributeValue: false,
  trimValues: true,
  isArray: (name) => {
    // These elements can appear multiple times
    return ['LocationGroup', 'CarrierGroup', 'FeeRule', 'Location', 'Carrier'].includes(name);
  },
};

function ensureArray(val) {
  if (!val) return [];
  return Array.isArray(val) ? val : [val];
}

function parseLocationGroup(element) {
  const tableNum = String(element.TableNum || '');
  const locations = ensureArray(element.Location).map(loc => {
    if (typeof loc === 'object') return loc['@_id'];
    return loc;
  }).filter(Boolean);
  return { tableNum, locations };
}

function parseCarrierGroup(element) {
  const tableNum = String(element.TableNum || '');
  const carriers = ensureArray(element.Carrier).map(c => {
    if (typeof c === 'object') return c['@_id'];
    return c;
  }).filter(Boolean);
  return { tableNum, carriers };
}

function parseFeeRule(element) {
  const carrier = element['@_carrier'] || '';
  const rule = { carrier };

  const fields = [
    'SequenceNum', 'Origin', 'Destination', 'FeeAmount', 'FeePercent',
    'OncePerBookingFeeAmount', 'TripType', 'CabinClass', 'CabinClassMatchMode',
    'PassengerType', 'MinPrice', 'MaxPrice', 'Interline', 'SliceInterline',
    'MinSegments', 'MinTotalSegments', 'LayoverMinutes', 'MaxHoursToDeparture',
    'PriceSource', 'TravelDateEffective', 'TravelDateDiscontinue',
    'TicketingDateEffective', 'TicketingDateDiscontinue', 'Suppress', 'RoundTrip'
  ];

  fields.forEach(field => {
    if (element[field] !== undefined && element[field] !== null) {
      const camelCase = field.charAt(0).toLowerCase() + field.slice(1);
      rule[camelCase] = String(element[field]);
    }
  });

  if (rule.sequenceNum) rule.sequenceNum = parseInt(rule.sequenceNum);
  if (rule.feePercent) rule.feePercent = parseFloat(rule.feePercent);
  if (rule.minSegments) rule.minSegments = parseInt(rule.minSegments);
  if (rule.minTotalSegments) rule.minTotalSegments = parseInt(rule.minTotalSegments);
  if (rule.layoverMinutes) rule.layoverMinutes = parseInt(rule.layoverMinutes);
  if (rule.maxHoursToDeparture) rule.maxHoursToDeparture = parseInt(rule.maxHoursToDeparture);

  return rule;
}

export function parseXmlToRuleSet(xmlString) {
  const parser = new XMLParser(parserOptions);
  const parsed = parser.parse(xmlString);

  if (!parsed.RuleSet) {
    throw new Error('No <RuleSet> element found in the XML file');
  }

  const ruleSet = parsed.RuleSet;

  const locationGroups = ensureArray(ruleSet.LocationGroup).map(parseLocationGroup);
  const carrierGroups = ensureArray(ruleSet.CarrierGroup).map(parseCarrierGroup);
  const feeRules = ensureArray(ruleSet.FeeRule).map(parseFeeRule);

  let testRuleSet = null;
  if (ruleSet.TestRuleSet) {
    const ts = ruleSet.TestRuleSet;
    testRuleSet = {
      locationGroups: ensureArray(ts.LocationGroup).map(parseLocationGroup),
      carrierGroups: ensureArray(ts.CarrierGroup).map(parseCarrierGroup),
      feeRules: ensureArray(ts.FeeRule).map(parseFeeRule),
    };
  }

  return { locationGroups, carrierGroups, feeRules, testRuleSet };
}

export function ruleSetToXml({ locationGroups, carrierGroups, feeRules, testRuleSet }) {
  const ruleSetObj = {};

  // Location Groups
  if (locationGroups.length > 0) {
    ruleSetObj.LocationGroup = locationGroups.map(g => ({
      TableNum: g.tableNum,
      Location: g.locations.map(id => ({ '@_id': id })),
    }));
  }

  // Carrier Groups
  if (carrierGroups.length > 0) {
    ruleSetObj.CarrierGroup = carrierGroups.map(g => ({
      TableNum: g.tableNum,
      Carrier: g.carriers.map(id => ({ '@_id': id })),
    }));
  }

  // Fee Rules
  if (feeRules.length > 0) {
    ruleSetObj.FeeRule = feeRules.map(buildFeeRuleObj);
  }

  // Test RuleSet
  if (testRuleSet && (testRuleSet.feeRules?.length > 0 || testRuleSet.carrierGroups?.length > 0 || testRuleSet.locationGroups?.length > 0)) {
    const testObj = {};
    if (testRuleSet.locationGroups?.length > 0) {
      testObj.LocationGroup = testRuleSet.locationGroups.map(g => ({
        TableNum: g.tableNum,
        Location: g.locations.map(id => ({ '@_id': id })),
      }));
    }
    if (testRuleSet.carrierGroups?.length > 0) {
      testObj.CarrierGroup = testRuleSet.carrierGroups.map(g => ({
        TableNum: g.tableNum,
        Carrier: g.carriers.map(id => ({ '@_id': id })),
      }));
    }
    if (testRuleSet.feeRules?.length > 0) {
      testObj.FeeRule = testRuleSet.feeRules.map(buildFeeRuleObj);
    }
    ruleSetObj.TestRuleSet = testObj;
  }

  const builder = new XMLBuilder({
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
    format: true,
    indentBy: '    ',
    suppressEmptyNode: true,
  });

  const xmlContent = builder.build({ RuleSet: ruleSetObj });
  return '<?xml version="1.0" encoding="UTF-8"?>\n' + xmlContent;
}

function buildFeeRuleObj(rule) {
  const obj = { '@_carrier': rule.carrier };

  const addField = (xmlName, value) => {
    if (value !== null && value !== undefined && value !== '') {
      obj[xmlName] = value;
    }
  };

  addField('SequenceNum', rule.sequenceNum);
  addField('TicketingDateEffective', rule.ticketingDateEffective);
  addField('TicketingDateDiscontinue', rule.ticketingDateDiscontinue);
  addField('TravelDateEffective', rule.travelDateEffective);
  addField('TravelDateDiscontinue', rule.travelDateDiscontinue);
  addField('Origin', rule.origin);
  addField('Destination', rule.destination);
  addField('FeeAmount', rule.feeAmount);
  addField('FeePercent', rule.feePercent);
  addField('OncePerBookingFeeAmount', rule.oncePerBookingFeeAmount);
  addField('Suppress', rule.suppress);
  addField('TripType', rule.tripType);
  addField('CabinClass', rule.cabinClass);
  addField('CabinClassMatchMode', rule.cabinClassMatchMode);
  addField('PassengerType', rule.passengerType);
  addField('MinPrice', rule.minPrice);
  addField('MaxPrice', rule.maxPrice);
  addField('Interline', rule.interline);
  addField('SliceInterline', rule.sliceInterline);
  addField('MinSegments', rule.minSegments);
  addField('MinTotalSegments', rule.minTotalSegments);
  addField('LayoverMinutes', rule.layoverMinutes);
  addField('MaxHoursToDeparture', rule.maxHoursToDeparture);
  addField('PriceSource', rule.priceSource);

  return obj;
}
